import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Share2, Clock, Calendar } from 'lucide-react';
import { motion } from 'framer-motion';
import { blogPosts } from '../data/mockData';
import ScrollReveal from '../components/ScrollReveal';
import Toast from '../components/Toast';

const BlogPost = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [showCopyAlert, setShowCopyAlert] = useState(false);
  
  const post = blogPosts.find(p => p.id === parseInt(id));
  
  // Find next post for navigation
  const currentIndex = blogPosts.findIndex(p => p.id === parseInt(id));
  const nextPost = blogPosts[(currentIndex + 1) % blogPosts.length];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  const handleShare = () => {
    navigator.clipboard.writeText(window.location.href);
    setShowCopyAlert(true);
    setTimeout(() => setShowCopyAlert(false), 3000);
  };


  if (!post) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-medium mb-4">Article not found</h2>
          <Link to="/blog" className="text-[#1a1a1a] underline">Back to Blog</Link>
        </div>
      </div>
    );
  }

  return (
    <main className="pt-24 pb-24">
      {/* Scroll Progress Bar could go here */}

      {/* Content Wrapper */}
      <div className="relative">
        {/* Hero Section */}
        <section className="max-w-[1400px] mx-auto px-6 lg:px-10 mb-12">
          <Link to="/blog" className="inline-flex items-center gap-2 text-sm text-[#5f6368] hover:text-[#1a1a1a] mb-8 transition-colors">
            <ArrowLeft size={16} /> Back to Blog
          </Link>
          
          <ScrollReveal>
            <div className="flex items-center gap-3 mb-6">
               <span className="px-3 py-1 rounded-full bg-[#f8f9fa] text-xs font-medium text-[#5f6368]">
                 {post.category}
               </span>
               <span className="flex items-center gap-1 text-xs text-[#5f6368]">
                 <Calendar size={12} /> {post.date}
               </span>
               <span className="flex items-center gap-1 text-xs text-[#5f6368]">
                 <Clock size={12} /> {post.readTime}
               </span>
            </div>

            <h1 
              className="text-4xl md:text-5xl lg:text-7xl text-[#1a1a1a] mb-10 tracking-tight max-w-5xl"
              style={{ fontFamily: "'Instrument Serif', serif", fontWeight: 400, lineHeight: 1.1 }}
            >
              {post.title}
            </h1>

            <div className="flex items-center justify-between border-t border-b border-[#e8eaed] py-6 mb-12">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#1a1a1a] flex items-center justify-center text-white text-xs">
                  {post.authorImage}
                </div>
                <div>
                  <p className="text-sm font-medium text-[#1a1a1a]">{post.author}</p>
                  <p className="text-xs text-[#5f6368]">Author</p>
                </div>
              </div>
              <button 
                onClick={handleShare}
                className="w-10 h-10 rounded-full border border-[#e8eaed] flex items-center justify-center hover:bg-[#f8f9fa] transition-colors text-[#1a1a1a]"
              >
                <Share2 size={18} />
              </button>
            </div>
          </ScrollReveal>

          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="aspect-video w-full rounded-2xl overflow-hidden mb-16"
          >
            <img 
              src={post.image} 
              alt={post.title} 
              className="w-full h-full object-cover"
            />
          </motion.div>
        </section>

        {/* Content */}
        <article className="max-w-[800px] mx-auto px-6 lg:px-10 mb-24">
          {post.content && post.content.map((block, index) => {
            switch (block.type) {
              case 'heading':
                return (
                  <ScrollReveal key={index}>
                    <h2 
                      className="text-2xl md:text-3xl text-[#1a1a1a] mt-12 mb-6"
                      style={{ fontFamily: "'Instrument Serif', serif" }}
                    >
                      {block.text}
                    </h2>
                  </ScrollReveal>
                );
              case 'paragraph':
                return (
                  <ScrollReveal key={index}>
                    <p className="text-lg text-[#333] leading-relaxed mb-6 font-light">
                      {block.text}
                    </p>
                  </ScrollReveal>
                );
              case 'quote':
                return (
                  <ScrollReveal key={index}>
                    <blockquote className="border-l-2 border-[#1a1a1a] pl-6 my-10 italic text-xl md:text-2xl text-[#1a1a1a]">
                      "{block.text}"
                    </blockquote>
                  </ScrollReveal>
                );
              case 'image':
                return (
                  <ScrollReveal key={index}>
                    <figure className="my-10">
                      <img 
                        src={block.src} 
                        alt={block.caption} 
                        className="w-full rounded-xl"
                      />
                      {block.caption && (
                        <figcaption className="text-center text-sm text-[#5f6368] mt-3">
                          {block.caption}
                        </figcaption>
                      )}
                    </figure>
                  </ScrollReveal>
                );
              default:
                return null;
            }
          })}
        </article>

        {/* Next Article */}
        <section className="bg-[#f8f9fa] py-20 border-t border-[#e8eaed]">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
            <span className="text-[#5f6368] text-xs tracking-wide uppercase mb-8 block">Up Next</span>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
               <div>
                 <h3 
                   className="text-3xl md:text-4xl lg:text-5xl text-[#1a1a1a] mb-6 cursor-pointer hover:underline decoration-1 underline-offset-4 transition-all"
                   style={{ fontFamily: "'Instrument Serif', serif" }}
                   onClick={() => navigate(`/blog/${nextPost.id}`)}
                 >
                   {nextPost.title}
                 </h3>
                 <p className="text-[#5f6368] text-lg mb-8 max-w-xl">{nextPost.excerpt}</p>
                 <Link to={`/blog/${nextPost.id}`} className="inline-flex items-center gap-2 bg-[#1a1a1a] text-white px-6 py-3 rounded-full text-sm font-medium hover:bg-[#333] transition-colors">
                   Read Article <ArrowRight size={16} />
                 </Link>
               </div>
               <div 
                 className="aspect-[16/10] rounded-2xl overflow-hidden cursor-pointer group"
                 onClick={() => navigate(`/blog/${nextPost.id}`)}
               >
                 <img 
                   src={nextPost.image} 
                   alt={nextPost.title} 
                   className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                 />
               </div>
            </div>
          </div>
        </section>
      </div>

      {/* Creative Copy Alert */}
      <Toast 
        isVisible={showCopyAlert} 
        message="Link copied to clipboard" 
      />
    </main>
  );
};

export default BlogPost;
